/**
 * Data hooks for the Users feature.
 *
 * Most user-management endpoints (list / search / count / document-url) are
 * not yet declared in the generated `paths` surface. Until the backend
 * regenerates the OpenAPI doc, we call them through `api.GET` with an
 * `as never` cast and tag the call sites with a TODO so the migration is
 * trivial once the types land.
 *
 * The single typed endpoint is `PATCH /api/v1/admin/users/{id}/document-status`,
 * which is consumed without any cast.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import { ApiError } from '@favelafood/shared/api/errors';
import type { components } from '@favelafood/shared/api/generated/api';

type UserResponse = components['schemas']['UserResponse'];
type RoleEnum = components['schemas']['RoleEnum'];
type DocumentStatus = components['schemas']['DocumentStatusEnum'];

/** Minimal pagination envelope shared with `PageResponse<T>` on the backend. */
export interface UserPage {
  content?: UserResponse[];
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  first?: boolean;
  last?: boolean;
}

export interface UserListFilters {
  /** Optional role filter. Uses {@link AdminRoleEnum} so STAFF can be filtered
   *  even though the generated `RoleEnum` still omits it. */
  role?: AdminRoleEnum;
  /** Optional active flag. */
  isActive?: boolean;
  /** Optional document status filter. */
  documentStatus?: DocumentStatus;
  /** Free-text search — composed as the `q` param alongside the other filters. */
  q?: string;
  page: number;
  size: number;
}

export const userQueryKeys = {
  all: ['users'] as const,
  list: (filters: UserListFilters) => ['users', 'list', filters] as const,
  documentUrl: (userId: string, type: string) =>
    ['users', 'documentUrl', userId, type] as const,
  roles: (userId: string) => ['users', 'roles', userId] as const,
};

/**
 * Full role set the admin panel can assign. The generated `RoleEnum` still
 * lags the backend (no `STAFF`), so the create/edit surfaces use this local
 * union instead. Lowercase variants are what the realm-roles endpoints speak.
 *
 * TODO: drop in favour of `components['schemas']['RoleEnum']` once the
 * regenerated OpenAPI doc carries STAFF.
 */
export type AdminRoleEnum = RoleEnum | 'STAFF';

/** Lowercase realm-role names the `/roles` endpoints accept/return. */
export type RealmRole = 'customer' | 'merchant' | 'courier' | 'staff' | 'admin';

/** The managed realm roles, in display order. Anything outside this set
 *  (e.g. Keycloak's `default-roles-favelafood`) is filtered out on render. */
export const MANAGED_REALM_ROLES: readonly RealmRole[] = [
  'customer',
  'merchant',
  'courier',
  'staff',
  'admin',
] as const;

const buildQueryString = (params: Record<string, unknown>): string => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return '';
  const usp = new URLSearchParams();
  entries.forEach(([k, v]) => {
    usp.set(k, String(v));
  });
  return `?${usp.toString()}`;
};

/**
 * Calls a path that exists on the backend but is missing from the generated
 * OpenAPI types. Cast the literal to `never` so `openapi-fetch` accepts it
 * — the runtime path is correct.
 *
 * TODO: drop the cast once `/api/v1/admin/users` (list/search/count) and
 * `/api/v1/admin/users/{id}/document-url` are emitted in `api.d.ts`.
 */
const callUntypedAdminUsers = async <T>(path: string): Promise<T> => {
  const result = await api.GET(path as never, {} as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
};

/**
 * POSTs an untyped admin-users path with a JSON body. Mirrors
 * `callUntypedAdminUsers` for writes — the `as never` casts keep the rest of
 * the codebase strict while the generated `paths` lack `POST /admin/users`.
 *
 * TODO: drop the cast once the OpenAPI doc emits `POST /api/v1/admin/users`.
 */
const postUntypedAdminUsers = async <T>(path: string, body: unknown): Promise<T> => {
  const result = await api.POST(path as never, { body } as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
};

/**
 * PATCHes an untyped admin-users path with a JSON body.
 *
 * TODO: drop the cast once `/admin/users/{id}/password` and
 * `/admin/users/{id}/roles` are emitted in api.d.ts.
 */
const patchUntypedAdminUsers = async <T>(path: string, body: unknown): Promise<T> => {
  const result = await api.PATCH(path as never, { body } as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
};

/**
 * DELETEs an untyped admin-users path. Mirrors the post/patch helpers for the
 * one write we do without a body (deactivate).
 *
 * TODO: drop the cast once `DELETE /api/v1/admin/users/{id}` is emitted in api.d.ts.
 */
const deleteUntypedAdminUsers = async <T>(path: string): Promise<T> => {
  const result = await api.DELETE(path as never, {} as never);
  const { data, error } = result as unknown as { data?: T; error?: unknown };
  if (error) {
    if (error instanceof Error) throw error;
    throw new ApiError(500, { message: 'Erro inesperado' }, 'HTTP 500');
  }
  return data as T;
};

export function useUsersList(filters: UserListFilters): UseQueryResult<UserPage, Error> {
  return useQuery<UserPage, Error>({
    queryKey: userQueryKeys.list(filters),
    queryFn: async () => {
      // The list endpoint now composes `q` with `role`/`isActive`/`documentStatus`
      // — there is no separate `/search` route. `q` is just another filter.
      const params: Record<string, unknown> = {
        page: filters.page,
        size: filters.size,
      };
      if (filters.q && filters.q.trim().length > 0) params.q = filters.q.trim();
      if (filters.role) params.role = filters.role;
      if (filters.isActive !== undefined) params.isActive = filters.isActive;
      if (filters.documentStatus) params.documentStatus = filters.documentStatus;
      return callUntypedAdminUsers<UserPage>(`/api/v1/admin/users${buildQueryString(params)}`);
    },
  });
}

export interface UseDocumentUrlResult {
  fetchUrl: (userId: string, type: 'face' | 'identity') => Promise<string>;
}

/**
 * Helper to fetch a presigned document URL on demand. Not memoized in
 * TanStack cache because URLs are short-lived (a few minutes) and requesting
 * twice is preferable to handing out a stale signed URL.
 */
export function useDocumentUrl(): UseDocumentUrlResult {
  return {
    fetchUrl: async (userId, type) => {
      const path = `/api/v1/admin/users/${encodeURIComponent(userId)}/document-url${buildQueryString({ type })}`;
      const data = await callUntypedAdminUsers<{ url?: string }>(path);
      if (!data.url) throw new Error('URL não disponível');
      return data.url;
    },
  };
}

export interface UpdateDocumentStatusParams {
  userId: string;
  status: DocumentStatus;
  reason?: string;
}

export interface UseUpdateDocumentStatusResult {
  update: (params: UpdateDocumentStatusParams) => Promise<UserResponse>;
  isPending: boolean;
}

export function useUpdateDocumentStatus(): UseUpdateDocumentStatusResult {
  const queryClient = useQueryClient();
  const mutation = useMutation<UserResponse, Error, UpdateDocumentStatusParams>({
    mutationFn: async ({ userId, status, reason }) => {
      // The generated schema accepts `{ status }`. `reason` is sent as a
      // best-effort field — the backend ignores unknown keys.
      const body: Record<string, unknown> = { status };
      if (reason && reason.trim().length > 0) body.reason = reason.trim();
      const { data, error } = await api.PATCH('/api/v1/admin/users/{id}/document-status', {
        params: { path: { id: userId } },
        body: body as { status: DocumentStatus },
      });
      if (error) throw error as Error;
      return data as UserResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });

  return {
    update: (params) => mutation.mutateAsync(params),
    isPending: mutation.isPending,
  };
}

/* ------------------------------------------------------------------ */
/* Invite back-office user (STAFF/ADMIN)                               */
/* ------------------------------------------------------------------ */

export interface InviteBackofficeInput {
  email: string;
  fullName?: string;
  /** Only STAFF/ADMIN are invitable — those are the back-office roles. */
  role: 'ADMIN' | 'STAFF';
}

export interface InviteBackofficeResult {
  /** `INVITED` for a brand-new account; `PROMOTED` when the email already
   *  existed and was elevated to the requested back-office role. */
  outcome: 'INVITED' | 'PROMOTED';
  userId: string;
  role: string;
}

/**
 * Invites (or promotes) a back-office user via
 * `POST /api/v1/admin/users/invite`. Returns `{ outcome, userId, role }`.
 * Invalidates the user list so the new/promoted row appears without a manual
 * refresh.
 */
export function useInviteBackoffice(): UseMutationResult<InviteBackofficeResult, Error, InviteBackofficeInput> {
  const queryClient = useQueryClient();
  return useMutation<InviteBackofficeResult, Error, InviteBackofficeInput>({
    mutationFn: (input) =>
      postUntypedAdminUsers<InviteBackofficeResult>('/api/v1/admin/users/invite', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Deactivate / reactivate                                             */
/* ------------------------------------------------------------------ */

/**
 * Deactivates a user via `DELETE /api/v1/admin/users/{id}` (soft delete —
 * returns 204). Invalidates the list so the row flips to inactive. The
 * backend guards self-deactivation with `CANNOT_DEACTIVATE_SELF`, which
 * surfaces as an `ApiError` for the caller to toast.
 */
export function useDeactivateUser(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId) =>
      deleteUntypedAdminUsers<void>(`/api/v1/admin/users/${encodeURIComponent(userId)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/**
 * Reactivates a user via `POST /api/v1/admin/users/{id}/reactivate` (returns
 * 204). Invalidates the list so the row flips back to active.
 */
export function useReactivateUser(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId) =>
      postUntypedAdminUsers<void>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/reactivate`,
        undefined,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Resend invite (Task 14 — OAuth/Keycloak hardening)                  */
/* ------------------------------------------------------------------ */

/**
 * Resends a pending STAFF/ADMIN invite via
 * `POST /api/v1/admin/users/{id}/resend-invite`. Returns 204 (no body) on
 * success. Backend errors (both 422 `BusinessException`): `INVITE_NOT_APPLICABLE`
 * (user isn't STAFF/ADMIN) and `INVITE_ALREADY_ACCEPTED` (nothing pending —
 * the invite was already completed) — the caller (UI) decides toast tone.
 */
export function useResendUserInvite(): UseMutationResult<void, Error, string> {
  return useMutation<void, Error, string>({
    mutationFn: (userId) =>
      postUntypedAdminUsers<void>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/resend-invite`,
        undefined,
      ),
  });
}

/* ------------------------------------------------------------------ */
/* Reset password (FAV-155)                                            */
/* ------------------------------------------------------------------ */

export interface ResetPasswordInput {
  userId: string;
  newPassword: string;
}

/**
 * Sets a new password via `PATCH /api/v1/admin/users/{id}/password`. Returns
 * 204 (no body) on success. No cache to invalidate — passwords aren't listed.
 */
export function useResetPassword(): UseMutationResult<void, Error, ResetPasswordInput> {
  return useMutation<void, Error, ResetPasswordInput>({
    mutationFn: ({ userId, newPassword }) =>
      patchUntypedAdminUsers<void>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/password`,
        { newPassword },
      ),
  });
}

/* ------------------------------------------------------------------ */
/* Realm roles (FAV-155)                                               */
/* ------------------------------------------------------------------ */

/**
 * Reads a user's realm roles via `GET /api/v1/admin/users/{id}/roles`. The
 * raw array may include Keycloak's `default-roles-favelafood`; callers filter
 * to {@link MANAGED_REALM_ROLES} when rendering. Disabled until a `userId`
 * is supplied so the dialog can mount before a row is selected.
 */
export function useUserRoles(userId: string | null): UseQueryResult<string[], Error> {
  return useQuery<string[], Error>({
    queryKey: userQueryKeys.roles(userId ?? ''),
    enabled: Boolean(userId),
    queryFn: () =>
      callUntypedAdminUsers<string[]>(
        `/api/v1/admin/users/${encodeURIComponent(userId ?? '')}/roles`,
      ),
  });
}

export interface UpdateUserRolesInput {
  userId: string;
  /** Desired set of lowercase realm roles. Empty -> backend 400 ROLES_REQUIRED. */
  roles: string[];
}

/**
 * Replaces a user's realm roles via `PATCH /api/v1/admin/users/{id}/roles`
 * with the desired set. Invalidates the roles cache (so the dialog reflects
 * the applied set) and the user list (the row's badge may change). Backend
 * validation errors (`ROLES_REQUIRED`, `ADMIN_CANNOT_SELF_DEMOTE`, unknown
 * role) surface as a 400 `ApiError`.
 */
export function useUpdateUserRoles(): UseMutationResult<string[], Error, UpdateUserRolesInput> {
  const queryClient = useQueryClient();
  return useMutation<string[], Error, UpdateUserRolesInput>({
    mutationFn: ({ userId, roles }) =>
      patchUntypedAdminUsers<string[]>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/roles`,
        { roles },
      ),
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.roles(userId) });
      void queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}
